/**
 * features/attendance/AttendanceRegisters.tsx
 * ------------------------------------------------------------------
 * Attendance "Registers" sub-tab. Pick one or many classes, a Week or
 * Month, and generate a printable register PDF from the existing
 * reportDailyAttendance data. Read-only — it never writes.
 *
 * Default output: ONE combined PDF, one class per page. Optional:
 * a ZIP of separate per-class PDFs (JSZip dynamic-imported, mirroring
 * BulkPayslips) for per-class distribution.
 *
 * Access: rendered only for roles the AttendanceModule already shows
 * the register tabs to (see AttendanceModule.visibleTabs) — no new
 * role, capability, or rule is introduced.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { FileText, Loader2, CalendarDays, Download, CheckSquare, Square } from 'lucide-react';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { canEnterClassCommentFor } from '@/lib/permissions';
import { isHoliday, holidayName } from '@/lib/holidays';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import {
  buildRegisterPdf,
  weekDaysFor,
  monthDaysFor,
  type RegisterClass,
  type RegisterDay,
  type RegisterPdfData,
} from '@/lib/registerPdf';
import type { AttendanceStatus, DailyAttendanceDoc } from '@/types';

const HOL = { isHoliday, holidayName };
const todayISO = () => new Date().toISOString().slice(0, 10);
const thisMonthISO = () => new Date().toISOString().slice(0, 7);
const fmt = (iso: string, opts: Intl.DateTimeFormatOptions) =>
  new Date(iso + 'T12:00:00').toLocaleDateString('en-GB', opts);

export default function AttendanceRegisters() {
  const { activeStudents: students, classes, teachers } = useData();
  const { userData } = useAuth();

  const [view, setView] = useState<'week' | 'month'>('week');
  const [weekAnchor, setWeekAnchor] = useState(todayISO());
  const [monthAnchor, setMonthAnchor] = useState(thisMonthISO());
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [asZip, setAsZip] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Classes this user may see registers for — same scoping the Entry tab uses.
  const myClasses = useMemo(
    () =>
      classes
        .filter((c) => canEnterClassCommentFor(userData, c.id, classes, teachers))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [classes, userData, teachers],
  );
  // Auto-select a lone class (e.g. a class teacher with one class).
  useEffect(() => {
    if (selectedIds.length === 0 && myClasses.length === 1) setSelectedIds([myClasses[0].id]);
  }, [selectedIds.length, myClasses]);

  const days = useMemo<RegisterDay[]>(
    () => (view === 'week' ? weekDaysFor(weekAnchor, HOL) : monthDaysFor(monthAnchor, HOL)),
    [view, weekAnchor, monthAnchor],
  );

  const periodLabel = useMemo(() => {
    if (view === 'week' && days.length) {
      const a = days[0];
      const b = days[days.length - 1];
      return `Week of ${fmt(a.date, { day: 'numeric', month: 'short' })}–${fmt(b.date, { day: 'numeric', month: 'short', year: 'numeric' })}`;
    }
    return fmt(monthAnchor + '-01', { month: 'long', year: 'numeric' });
  }, [view, days, monthAnchor]);

  const periodSlug = view === 'week' ? (days[0]?.date ?? weekAnchor) : monthAnchor;
  const holidayCount = days.filter((d) => d.holiday).length;

  function toggleClass(id: string) {
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  }
  const allSelected = myClasses.length > 0 && selectedIds.length === myClasses.length;
  const toggleAll = () => setSelectedIds(allSelected ? [] : myClasses.map((c) => c.id));

  // Build one class's register data: surname-first fullNames sort naturally
  // by localeCompare (= surname order), matching the codebase convention.
  async function buildClassData(cid: string): Promise<RegisterClass | null> {
    const cls = classes.find((c) => c.id === cid);
    if (!cls) return null;
    const roster = students
      .filter((s) => s.currentClass === cid)
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
    const marks: Record<string, Record<string, AttendanceStatus>> = {};
    for (const d of days) {
      if (d.holiday) continue; // no register kept on holidays
      const snap = await getDoc(doc(db, COLLECTIONS.dailyAttendance, `${cid}_${d.date}`));
      if (!snap.exists()) continue;
      const data = snap.data() as DailyAttendanceDoc;
      for (const [sid, st] of Object.entries(data.entries || {})) {
        (marks[sid] ||= {})[d.date] = st;
      }
    }
    return {
      classId: cid,
      className: cls.name,
      students: roster.map((s) => ({
        studentId: s.id,
        fullName: s.fullName,
        gender: s.gender === 'F' ? 'F' : 'M',
      })),
      marks,
    };
  }

  async function generate() {
    if (selectedIds.length === 0) {
      setMsg({ ok: false, text: 'Pick at least one class.' });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      const logo = await loadLogoDataUrl();
      const base: Omit<RegisterPdfData, 'classes'> = {
        view,
        periodLabel,
        days,
        logoDataUrl: logo,
        generatedBy: userData?.displayName || undefined,
      };
      // Preserve the display order of myClasses among the selection.
      const orderedIds = myClasses.filter((c) => selectedIds.includes(c.id)).map((c) => c.id);
      const built = (await Promise.all(orderedIds.map((id) => buildClassData(id)))).filter(
        (c): c is RegisterClass => !!c && c.students.length > 0,
      );
      if (built.length === 0) {
        setMsg({ ok: false, text: 'No students found in the selected class(es).' });
        return;
      }

      if (asZip) {
        // Separate per-class PDFs, zipped (JSZip dynamic-imported like BulkPayslips).
        const { default: JSZip } = await import('jszip');
        const zip = new JSZip();
        for (const cd of built) {
          const pdf = buildRegisterPdf({ ...base, classes: [cd] });
          zip.file(`Register_${cd.className.replace(/\s+/g, '_')}_${periodSlug}.pdf`, pdf.output('blob'));
        }
        const out = await zip.generateAsync({ type: 'blob' });
        const url = URL.createObjectURL(out);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Registers_${periodSlug}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        setMsg({ ok: true, text: `Generated ${built.length} register${built.length === 1 ? '' : 's'} (ZIP of separate PDFs).` });
      } else {
        // One combined PDF, one class per page.
        const pdf = buildRegisterPdf({ ...base, classes: built });
        pdf.save(built.length === 1 ? `Register_${built[0].className.replace(/\s+/g, '_')}_${periodSlug}.pdf` : `Registers_${periodSlug}.pdf`);
        setMsg({ ok: true, text: `Generated a combined register — ${built.length} class${built.length === 1 ? '' : 'es'}, one per page.` });
      }
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  const inputCls =
    'w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white';

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      <div className="flex items-center gap-2 p-5 border-b border-gray-100 dark:border-gray-700">
        <FileText size={18} className="text-[#2d5295]" />
        <h2 className="font-semibold text-gray-900 dark:text-white">Attendance Registers</h2>
      </div>
      <div className="p-5 space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Generate a printable register from the daily attendance already captured on the Entry tab.
          Choose a week or month and one or more classes; holidays are shaded, and Boys/Girls present
          totals appear under each class. Names are in surname order.
        </p>

        {/* View toggle + period */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">View</label>
            <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden">
              {(['week', 'month'] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => setView(v)}
                  className={`px-4 py-2 text-sm font-medium capitalize ${
                    view === v ? 'bg-[#2d5295] text-white' : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              {view === 'week' ? 'Any day in the week' : 'Month'}
            </label>
            {view === 'week' ? (
              <input type="date" value={weekAnchor} max={todayISO()} onChange={(e) => setWeekAnchor(e.target.value)} className={inputCls} />
            ) : (
              <input type="month" value={monthAnchor} onChange={(e) => setMonthAnchor(e.target.value)} className={inputCls} />
            )}
          </div>
        </div>

        {/* Class multi-select */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
              Classes ({selectedIds.length} selected)
            </label>
            {myClasses.length > 1 && (
              <button onClick={toggleAll} className="text-xs text-[#2d5295] hover:underline">
                {allSelected ? 'Clear all' : 'Select all'}
              </button>
            )}
          </div>
          {myClasses.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No classes available to you.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5 max-h-56 overflow-y-auto p-1">
              {myClasses.map((c) => {
                const on = selectedIds.includes(c.id);
                return (
                  <button
                    key={c.id}
                    onClick={() => toggleClass(c.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left ${
                      on
                        ? 'border-[#2d5295] bg-[#2d5295]/5 text-[#2d5295] dark:text-blue-200'
                        : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {on ? <CheckSquare size={15} /> : <Square size={15} className="opacity-50" />}
                    <span className="truncate">{c.name}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Output mode + generate */}
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <input type="checkbox" checked={asZip} onChange={(e) => setAsZip(e.target.checked)} className="rounded" />
            Separate PDFs (ZIP)
          </label>
          <button
            onClick={generate}
            disabled={busy || selectedIds.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#2d5295] text-white text-sm font-semibold hover:bg-[#1b3a6b] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : asZip ? <Download size={15} /> : <FileText size={15} />}
            {asZip ? 'Generate ZIP' : selectedIds.length > 1 ? 'Generate combined PDF' : 'Generate register PDF'}
          </button>
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
            <CalendarDays size={13} /> {periodLabel} · {days.length} school day{days.length === 1 ? '' : 's'}
            {holidayCount > 0 ? ` · ${holidayCount} holiday${holidayCount === 1 ? '' : 's'} shaded` : ''}
          </span>
        </div>

        {msg && (
          <div className={`text-sm rounded-xl px-4 py-3 ${msg.ok ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
            {msg.text}
          </div>
        )}

        <p className="text-xs text-gray-400">
          Registers read existing data only — nothing is written. Public holidays are the hardcoded 2026 set
          (holidays.ts); extend it when the calendar year rolls over.
        </p>
      </div>
    </div>
  );
}
